// KPI Builder — learning-progress comparisons. Shapes of the live “Enrolled vs Registered”,
// “Competency-wise Quality Assessment” and “Course Effectiveness” KPIs, each drawn by a backend handler.
import { INDEX_PREFIX, KPI_FILTERS, SOURCES } from 'constants/kpiBuilderCatalogue';
import { envelope, filterFor, RecipeContext, RecipeResult } from './queries';
import { BuilderAnswers, EsNode, ProgressKind, QueryObject, SourceId } from './types';

export interface ProgressKindInfo {
  label: string;
  desc: string;
  /** Short name used in the title. */
  title: string;
  /** One sentence for the summary panel. */
  summary: string;
  /** Bar names the dashboard shows, as seen on the live dashboard. Empty when not checked. */
  bars: string[];
  /** The two data sources the comparison reads. */
  src: SourceId;
  srcB: SourceId;
}

export const PROGRESS_KINDS: Record<ProgressKind, ProgressKindInfo> = {
  coverage: {
    bars: [], label: 'Registered vs enrolled, per district', title: 'Enrolled vs registered learners by district', src: 'reg', srcB: 'enr',
    desc: 'Answers: how many registered learners went on to enrol, in each district. You get: two bars per district, registered next to enrolled, named by the dashboard.',
    summary: 'For each district, shows how many learners registered next to how many enrolled in a course',
  },
  quality: {
    bars: ['Activation', 'Pass', 'Dropout'], label: 'Enrolled vs certified, per competency', title: 'Competency-wise quality assessment', src: 'enr', srcB: 'pass',
    desc: 'Answers: of the learners who enrolled in each competency, how many achieved a level and how many dropped out. You get: three bars per competency, Activation (enrolled), Pass and Dropout. This is the “Competency-wise Quality Assessment” chart.',
    summary: 'For each competency, shows Activation (learners who enrolled), Pass (learners who achieved a level) and Dropout',
  },
  course: {
    bars: ['Completed & Passed', 'Completed & Not Passed', 'Not Yet Attempted'], label: 'How well each course works', title: 'Course effectiveness by competency', src: 'pass', srcB: 'assess',
    desc: 'Answers: how well each competency or course turns learners into completers. You get: one stacked bar per competency or course, with Completed & Passed, Completed & Not Passed and Not Yet Attempted as shares of the whole.',
    summary: 'For each competency or course, shows the share of learners who completed and passed, completed and did not pass, or have not yet attempted',
  },
};

/** Course effectiveness can show one bar per course (the live KPI) or per competency, named by the competency. */
export const perCompetency = (a: BuilderAnswers): boolean => a.progressKind === 'course' && a.group !== 'course';
export const progressTitle = (a: BuilderAnswers): string =>
  (a.progressKind === 'course' ? `Course effectiveness by ${perCompetency(a) ? 'competency' : 'course'}` : PROGRESS_KINDS[a.progressKind].title);

const sizedTerms = (field: string, size: number): EsNode => ({ terms: { field, size } });
const professionOnly = (c: RecipeContext, id: SourceId): EsNode => filterFor(id, c.a, true);

// shape of enrolledvsregistered
function buildCoverage(c: RecipeContext): RecipeResult {
  const side = (id: SourceId, agg: string, users: string, filter: EsNode, date: string): QueryObject => {
    const aggs = { [agg]: { filter, aggs: { districts: { terms: { field: 'district', size: 38, order: { _key: 'asc' } }, aggs: { [users]: { cardinality: { field: 'userId' } } } } } } };
    const path = `aggregations.${agg}.districts.buckets`;
    return { ...envelope(id, c.a), dateRefField: date, filterPath: `${path}.key,${path}.${users}.value`, aggrQuery: { size: 0, aggs } };
  };
  const enrolled = { bool: { filter: [professionOnly(c, 'enr'), { exists: { field: 'competencyId' } }, { bool: { must_not: [{ term: { competencyId: '' } }] } }] } };
  return {
    queries: [side('reg', 'reg_by_district', 'reg_users', filterFor('reg', c.a), SOURCES.reg.date), side('enr', 'enrolled_by_district', 'enrolled_users', enrolled, SOURCES.enr.date)],
    cfg: { chartType: 'enrolledvsregistered', aggregationPaths: ['reg_by_district', 'enrolled_by_district'] },
    handlerKey: 'enrolledvsregistered',
  };
}

const byCompetency = (field: string): Record<string, EsNode> => ({
  by_competency: { ...sizedTerms(field, 500), aggs: { user_ids: sizedTerms('userId', 20000), comp_name: sizedTerms('competencyName.keyword', 1) } },
});

/** The date fields these indices actually have: the Timestamp versions, the same as every other question and the existing charts. */
export const QUALITY_DATES = { enr: 'enrolledDateTimestamp', pass: 'passbookEffectiveDateTimestamp' };

// shape of competencyqualityassessment: no location filters, matched by competency
function buildQuality(c: RecipeContext): RecipeResult {
  const side = (id: SourceId, agg: string, field: string, date: string): QueryObject => ({
    ...envelope(id, c.a), requestQueryMap: {}, dateRefField: date, aggrQuery: { aggs: { [agg]: { filter: filterFor(id, c.a), aggs: byCompetency(field) } } },
  });
  return {
    queries: [side('enr', 'activationByCompetency', 'competencyId', QUALITY_DATES.enr), side('pass', 'passByCompetency', 'competencyID', QUALITY_DATES.pass)],
    cfg: { chartType: 'competencyqualityassessment', aggregationPaths: ['activationByCompetency', 'passByCompetency'], headerLabel: 'Metric', valueLabel: 'Count' },
    handlerKey: 'competencyqualityassessment',
  };
}

const COURSE_FILTER_KEYS = ['level', 'competency'];
const users = (): Record<string, EsNode> => ({ user_ids: sizedTerms('userId', 20000) });

// shape of courseeffectivenesslevelwise
function buildCourse(c: RecipeContext): RecipeResult {
  // The handler reads the buckets named by_course; the bucket key is what names each bar.
  const barField = perCompetency(c.a) ? 'competencyName.keyword' : 'courseId';
  const side = (id: SourceId, map: Record<string, string>): Omit<QueryObject, 'aggrQuery'> => ({ ...envelope(id, c.a), requestQueryMap: { ...envelope(id, c.a).requestQueryMap, Course: 'courseId', ...map } });
  const passbook: QueryObject = {
    ...side('pass', { Level: 'levels', Competency: 'competencyID' }), dateRefField: QUALITY_DATES.pass, filterPath: 'aggregations.passbookAgg.by_course.buckets',
    aggrQuery: { size: 0, aggs: { passbookAgg: { filter: filterFor('pass', c.a), aggs: { by_course: { ...sizedTerms(barField, 600), aggs: { course_name: sizedTerms(barField === 'courseId' ? 'courseName.keyword' : 'competencyName.keyword', 1), level: sizedTerms('levels', 1), ...users() } } } } } },
  };
  const assess: QueryObject = {
    ...side('assess', { Competency: 'competencyId' }), filterPath: 'aggregations.assessmentAgg.by_course.buckets', indexName: INDEX_PREFIX + SOURCES.assess.index,
    aggrQuery: { size: 0, aggs: { assessmentAgg: { filter: filterFor('assess', c.a), aggs: { by_course: { ...sizedTerms(barField, 600), aggs: {
      passed_users: { filter: { term: { isPass: true } }, aggs: users() }, attempted_users: { filter: { match_all: {} }, aggs: users() },
    } } } } } },
  };
  return {
    queries: [passbook, assess],
    cfg: {
      chartType: 'courseeffectivenesslevelwise', valueType: 'percentage', orientation: 'horizontal', enableChartSearch: true, aggregationPaths: ['passbookAgg', 'assessmentAgg'],
      kpiFilters: COURSE_FILTER_KEYS.map((id) => KPI_FILTERS.find((f) => f.id === id)?.def),
    },
    handlerKey: 'courseeffectivenesslevelwise',
  };
}

const BUILDERS: Record<ProgressKind, (c: RecipeContext) => RecipeResult> = { coverage: buildCoverage, quality: buildQuality, course: buildCourse };

export const buildProgress = (c: RecipeContext): RecipeResult => BUILDERS[c.a.progressKind](c);
